import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

import { authService } from '../services/auth.service';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasAccess: boolean;
  accessDenied: boolean;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const lastCheckRef = useRef(0);

  const sameUser = (a: User | null, b: User | null): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.id === b.id && (a.roles || []).join(',') === (b.roles || []).join(',');
  };

  /**
   * `silent` keeps background re-validation from toggling isLoading: ProtectedRoute
   * swaps out the whole app while loading, so a non-silent check unmounts the page
   * and drops whatever the user was doing.
   */
  const runAuthCheck = async (silent: boolean) => {
    if (!silent) setIsLoading(true);
    try {
      const status = await authService.checkAuthStatus();
      lastCheckRef.current = Date.now();

      if (status.authenticated && status.user) {
        const nextUser = status.user;
        setUser(prev => (sameUser(prev, nextUser) ? prev : nextUser));
        setAccessDenied(!authService.hasProviderValidatorAccess(nextUser));
      } else {
        setUser(null);
        setAccessDenied(false);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
      if (!silent) {
        setUser(null);
        setAccessDenied(false);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const checkAuth = async () => runAuthCheck(false);

  useEffect(() => {
    runAuthCheck(false);

    // Re-validate when the user returns to the tab or on a light interval, so an
    // idle window notices an expired/refreshed session without a manual reload.
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckRef.current < 30 * 1000) return;
      runAuthCheck(true);
    };

    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    const intervalId = window.setInterval(revalidate, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = () => {
    const API_URL = window.location.origin.includes('localhost')
      ? 'http://localhost:5004'
      : '';
    const returnUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${API_URL}/auth/login?returnUrl=${returnUrl}`;
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setAccessDenied(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasAccess: !!user && authService.hasProviderValidatorAccess(user),
    accessDenied,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
